import React, { useState } from "react";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * QrTokenMintForm — Sub-sprint C (RL-FRONTEND-SHELL-V0).
 *
 * Controlled form for `POST /api/admin/qr-tokens`. v0 surfaces only the
 * operative kinds whitelisted by the contract; reserved (`system`) and
 * blocked (`worker_self_open`) kinds are filtered client-side as
 * defence-in-depth. The mutation is invoked by the parent wrapped in
 * `ConfirmActionDialog`.
 *
 * Track A pilot: `issued_for_organisation_id` is pre-populated to the
 * pilot org and disabled in v0.
 */
const ALLOWED_KINDS = [
  "worker_personal",
  "customer_checkin",
  "customer_handover",
  "equipment",
  "shop",
  "ticket",
  "event_checkin",
];

export const QrTokenMintForm = ({
  pilotOrgId = "TEAMSOFTWAREINNOVATIONS",
  value,
  onChange,
  disabled = false,
}) => {
  const t = useRacketLabAdminT();
  const [local, setLocal] = useState(
    value || { kind: "ticket", issued_for_organisation_id: pilotOrgId, entity_id: "", ttl_seconds: "" },
  );
  const update = (k, v) => {
    const next = { ...local, [k]: v };
    setLocal(next);
    onChange?.(next);
  };

  return (
    <div data-testid="rl-qr-mint-form" className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">{t("qr_tokens.mint.kind_label")}</span>
        <select
          data-testid="rl-qr-mint-kind-select"
          value={local.kind}
          onChange={(e) => update("kind", e.target.value)}
          disabled={disabled}
          className="rounded-md border border-slate-300 px-2 py-1 text-sm"
        >
          {ALLOWED_KINDS.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">{t("qr_tokens.mint.org_label_locked")}</span>
        <input
          data-testid="rl-qr-mint-org-input"
          value={local.issued_for_organisation_id}
          disabled
          className="rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-sm text-slate-500"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">{t("qr_tokens.mint.entity_label_optional")}</span>
        <input
          data-testid="rl-qr-mint-entity-id-input"
          value={local.entity_id || ""}
          onChange={(e) => update("entity_id", e.target.value)}
          disabled={disabled}
          placeholder={t("qr_tokens.mint.entity_placeholder")}
          className="rounded-md border border-slate-300 px-2 py-1 text-sm"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-700">{t("qr_tokens.mint.ttl_label_optional")}</span>
        <input
          data-testid="rl-qr-mint-ttl-input"
          value={local.ttl_seconds || ""}
          onChange={(e) => update("ttl_seconds", e.target.value)}
          type="number"
          min={60}
          disabled={disabled}
          placeholder={t("qr_tokens.mint.ttl_placeholder")}
          className="rounded-md border border-slate-300 px-2 py-1 text-sm"
        />
      </label>

      <p className="text-[10px] text-slate-500">
        {t("qr_tokens.mint.footer_note")}
      </p>
    </div>
  );
};

export default QrTokenMintForm;
